import { useMemo } from "react";
import { Tooltip, Typography, Grid, styled } from "@mui/material";
import BookingIcon from "./booking_icon.svg?react";
import { IPairing } from "@vmosolutions/oc-interfaces";
import { useFormikContext } from "formik";

interface IBookingIdent {
  _id?: string;
  locator?: string;
  crew?: string[]; // Array of crew object ids that booking are assigned to.
}

interface Props {
  bookings: IBookingIdent[];
  pairing: IPairing;
}

interface IBookingWithCrewInfo extends IBookingIdent {
  crewNames: string;
  displayOrder: number;
}

const OCLocatorChip = styled("div")(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  margin: "0px 2px",
  "& svg": { height: 10, margin: "0px 2px" },
}));

const sortByDisplayOrder = (a: IBookingWithCrewInfo, b: IBookingWithCrewInfo): number =>
  a.displayOrder - b.displayOrder;

/**
 * Booking Locator Chips
 */
export const LocatorChips = (props: Props): JSX.Element => {
  const { bookings, pairing } = props;

  /**
   * Add crew information to booking including names of assigned crew and display order info.
   */
  const bookingsWithCrewInfo: IBookingWithCrewInfo[] = useMemo(() => {
    const sortedCrew = pairing?.positions?.map((pos) => {
      return {
        crewID: pos?.crew?._id,
        name: `${pos.crew?.firstName} ${pos.crew?.lastName}`,
        displayOrder: pos?.position?.displayOrder ?? 99, // if unknown push to back
      };
    });
    // console.log("sortedCrew", sortedCrew);
    return bookings?.reduce((acc, booking) => {
      const crewOnBooking =
        booking?.crew?.length > 0 ? sortedCrew?.filter((crew) => booking?.crew?.includes(crew?.crewID)) : [];
      if (crewOnBooking?.length > 0) {
        const crewNames = crewOnBooking?.map((c) => c.name);
        acc.push({ ...booking, crewNames: crewNames.join(", "), displayOrder: crewOnBooking?.[0].displayOrder });
      } else {
        acc.push({ ...booking, crewNames: undefined, displayOrder: undefined });
      }
      return acc;
    }, []);
  }, [pairing?.positions, bookings]);

  return (
    <Grid container className="bookings_container">
      {bookingsWithCrewInfo?.length > 0 ? (
        bookingsWithCrewInfo.sort(sortByDisplayOrder).map((booking) => {
          const mismatchedCrew = !booking?.crewNames;

          return (
            <div key={booking?._id}>
              <Tooltip
                enterDelay={500}
                placement="top"
                title={booking?.crewNames ?? "No matching crew found on booking"}
              >
                <OCLocatorChip id={"booking_" + booking?._id} style={{ color: mismatchedCrew ? "red" : "white" }}>
                  <>
                    <BookingIcon className="booking_icon" />
                    {booking?.locator}
                  </>
                </OCLocatorChip>
              </Tooltip>
            </div>
          );
        })
      ) : (
        <Typography variant="caption">No Booking</Typography>
      )}
    </Grid>
  );
};
